import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import { mapTeacherId } from '../utils/teacherIdMapper';
import PaymentForm from '../components/PaymentForm';
import { 
  FaCalendarAlt, 
  FaClock, 
  FaDollarSign, 
  FaUser, 
  FaMapMarkerAlt, 
  FaStar, 
  FaArrowLeft,
  FaCreditCard,
  FaPaypal,
  FaCheckCircle,
  FaTimes,
  FaInfoCircle,
  FaSpinner
} from 'react-icons/fa';

interface Teacher {
  _id: string;
  basicInfo: {
    fullName: string;
    country: string;
    profilePicture?: string;
  };
  rates: {
    hourlyRate: number;
    currency: string;
    offersFreeTrial: boolean;
  };
  stats: {
    averageRating: number;
    totalReviews: number;
  };
  profile: {
    bio: string;
  };
  teachingOffer: {
    targetStudents: string[];
  };
}

interface BookingForm {
  scheduledDate: string;
  scheduledTime: string;
  duration: number;
  sessionType: 'regular' | 'trial';
  notes: string;
}

const TeacherSessionPayment: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingForm, setBookingForm] = useState<BookingForm>({
    scheduledDate: '',
    scheduledTime: '',
    duration: 60,
    sessionType: 'regular',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchTeacher();
  }, [teacherId, user, navigate]);

  const fetchTeacher = async () => {
    try {
      setLoading(true);
      
      // Mock teacher data for demonstration - using the same data from TeacherProfile
      const mockTeachers: { [key: string]: Teacher } = {
        '5': {
          _id: '5',
          basicInfo: {
            fullName: 'Bahar Erfanian',
            country: 'United States',
            profilePicture: '/images/profiles/bahar-erfanian.jpg'
          },
          rates: {
            hourlyRate: 28,
            currency: 'USD',
            offersFreeTrial: true
          },
          stats: {
            averageRating: 4.8,
            totalReviews: 45
          },
          profile: {
            bio: 'As a computer science professional with deep Persian cultural roots, I bring a unique perspective to Persian language teaching.'
          },
          teachingOffer: {
            targetStudents: ['Beginner', 'Intermediate', 'Advanced', 'Professional learners', 'Heritage speakers']
          }
        }
      };

      const mockTeacher = mockTeachers[teacherId || ''];
      if (mockTeacher) {
        setTeacher(mockTeacher);
        setError(null);
      } else {
        setError('Teacher not found');
      }
    } catch (error) {
      console.error('Error fetching teacher:', error);
      setError('Failed to load teacher information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof BookingForm, value: string | number) => {
    setBookingForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateTotal = () => {
    if (!teacher) return 0;
    const rate = bookingForm.sessionType === 'trial' && teacher.rates.offersFreeTrial 
      ? 0 
      : teacher.rates.hourlyRate;
    return (rate * bookingForm.duration) / 60;
  };

  const validateForm = () => {
    if (!bookingForm.scheduledDate) {
      setError('Please select a date');
      return false;
    }
    if (!bookingForm.scheduledTime) {
      setError('Please select a time');
      return false;
    }
    if (bookingForm.duration < 15) {
      setError('Session duration must be at least 15 minutes');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock session creation
      const mockSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(mockSessionId);
      
      // If payment is required, show payment form
      if (calculateTotal() > 0) {
        setShowPayment(true);
      } else {
        // Free trial - redirect to success
        navigate(`/session-success/${mockSessionId}`);
      }
    } catch (error) {
      console.error('Error booking session:', error);
      setError('Failed to book session. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = (paymentData: any) => {
    // Update session with payment information
    if (sessionId) {
      // This would typically update the session with payment confirmation
      navigate(`/session-success/${sessionId}`);
    }
  };

  const handlePaymentError = (error: string) => {
    setError(error);
    setShowPayment(false);
  };

  const handlePaymentCancel = () => {
    setShowPayment(false);
  };

  const getAvailableTimes = () => {
    const times = [];
    for (let hour = 9; hour <= 20; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        times.push(time);
      }
    }
    return times;
  };

  const getMinDate = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading teacher information...</p>
        </div>
      </div>
    );
  }

  if (error && !teacher) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{error}</h3>
          <button
            onClick={() => navigate('/teachers')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Teachers
          </button>
        </div>
      </div>
    );
  }

  if (!teacher) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/teacher/${teacherId}`)}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <FaArrowLeft className="mr-2" />
            Back to Teacher Profile
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Book Session & Payment</h1>
          <p className="text-gray-600 mt-2">Complete your session booking and payment</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Teacher Info */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center mb-6">
              {teacher.basicInfo.profilePicture ? (
                <img
                  src={teacher.basicInfo.profilePicture}
                  alt={teacher.basicInfo.fullName}
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
              ) : (
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <FaUser className="text-blue-600 text-2xl" />
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{teacher.basicInfo.fullName}</h2>
                <div className="flex items-center text-gray-600">
                  <FaMapMarkerAlt className="mr-1" />
                  <span>{teacher.basicInfo.country}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <FaStar className="text-yellow-400 mr-1" />
                  <span>{teacher.stats.averageRating.toFixed(1)} ({teacher.stats.totalReviews} reviews)</span>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-900 mb-2">Rate</h3>
              <div className="flex items-center text-2xl font-bold text-green-600">
                <FaDollarSign className="mr-1" />
                {teacher.rates.hourlyRate}
                <span className="text-sm text-gray-600 ml-1">/hour</span>
              </div>
              {teacher.rates.offersFreeTrial && (
                <div className="mt-2 text-sm text-green-600">
                  <FaCheckCircle className="inline mr-1" />
                  Offers free trial session
                </div>
              )}
            </div>
          </div>

          {/* Booking Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Session Details</h3>
            
            <div className="space-y-4">
              {/* Date Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaCalendarAlt className="inline mr-2" />
                  Date
                </label>
                <input
                  type="date"
                  value={bookingForm.scheduledDate}
                  onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                  min={getMinDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Time Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaClock className="inline mr-2" />
                  Time
                </label>
                <select
                  value={bookingForm.scheduledTime}
                  onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a time</option>
                  {getAvailableTimes().map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>

              {/* Duration Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaClock className="inline mr-2" />
                  Duration
                </label>
                <select
                  value={bookingForm.duration}
                  onChange={(e) => handleInputChange('duration', Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>

              {/* Session Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Type
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="regular"
                      checked={bookingForm.sessionType === 'regular'}
                      onChange={(e) => handleInputChange('sessionType', e.target.value as 'regular' | 'trial')}
                      className="mr-2"
                    />
                    <span>Regular Session</span>
                  </label>
                  {teacher.rates.offersFreeTrial && (
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="trial"
                        checked={bookingForm.sessionType === 'trial'}
                        onChange={(e) => handleInputChange('sessionType', e.target.value as 'regular' | 'trial')}
                        className="mr-2"
                      />
                      <span>Free Trial</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={bookingForm.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Any specific topics or questions you'd like to cover..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Total Cost */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium text-gray-900">Total Cost:</span>
                  <span className="text-2xl font-bold text-green-600">
                    ${calculateTotal().toFixed(2)} {teacher.rates.currency}
                  </span>
                </div>
                {teacher.rates.offersFreeTrial && bookingForm.sessionType === 'trial' && (
                  <div className="mt-2 text-sm text-green-600">
                    <FaCheckCircle className="inline mr-1" />
                    Free trial session!
                  </div>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  'Continue to Payment'
                )}
              </button>

              <div className="text-sm text-gray-600">
                <div className="flex items-start mb-2">
                  <FaInfoCircle className="text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>You'll receive a confirmation email with meeting details after booking.</span>
                </div>
                <div className="flex items-start">
                  <FaInfoCircle className="text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Sessions can be cancelled up to 24 hours before the scheduled time.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Modal */}
        {showPayment && sessionId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <PaymentForm
              amount={calculateTotal()}
              currency={teacher.rates.currency}
              sessionId={sessionId}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              onCancel={handlePaymentCancel}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherSessionPayment;
